
import { AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Facebook, Instagram, Linkedin, Phone, Mail, MessageCircle, Youtube, Calendar, MessageSquare, Info, List } from "lucide-react";
import { WelcomeMessage } from "./contact-widget/WelcomeMessage";
import { FloatingButton } from "./contact-widget/FloatingButton";
import { ContactForm } from "./contact-widget/ContactForm";
import { AIChatDialog } from "./contact-widget/AIChatDialog";
import { useContactWidget } from "./contact-widget/useContactWidget";
import { ContactOption, ContactWidgetProps, WidgetPage } from "./types/contact-widget";
import { motion } from "framer-motion";
import { FAQAccordion } from "./FAQAccordion";
import { useState } from "react";

export const ContactWidget = ({ profile }: ContactWidgetProps) => {
  const {
    open,
    setOpen,
    showText,
    formType,
    setFormType,
    submitting,
    formData,
    setFormData,
    handleEmailClick,
    handleSubmit,
    handleChange,
    getAvailableFormTypes
  } = useContactWidget(profile);

  const [currentPage, setCurrentPage] = useState<WidgetPage>('contact');

  const primaryColor = profile.primary_color || '#4B89FF';
  const secondaryColor = profile.secondary_color || '#7E69AB';
  const availableFormTypes = getAvailableFormTypes();

  const contactOptions: ContactOption[] = [
    profile.show_scheduling && profile.show_booking_form && {
      id: 'booking',
      icon: <Calendar className="h-6 w-6 text-white" />,
      label: "Schedule Meeting",
      sublabel: "Book a time",
      action: () => setFormType('booking'),
      iconBgColor: "bg-gradient-to-r from-blue-600 to-purple-600",
      gradientColors: "from-blue-500/10 via-purple-500/5 to-transparent",
      isPrimary: true
    },
    profile.show_lead_form && {
      id: 'lead',
      icon: <MessageCircle className="h-6 w-6 text-white" />,
      label: "Leave a Message",
      sublabel: "Get in touch",
      action: () => setFormType('lead'),
      iconBgColor: "bg-gradient-to-r from-purple-600 to-blue-600",
      gradientColors: "from-purple-500/10 via-blue-500/5 to-transparent",
      isPrimary: true
    },
    profile.email && {
      id: 'email',
      icon: <Mail className="h-6 w-6 text-white" />,
      label: "Email",
      sublabel: profile.email,
      action: handleEmailClick,
      iconBgColor: "bg-blue-500",
      gradientColors: "from-blue-500/10 via-blue-500/5 to-transparent"
    },
    profile.phone && profile.call_sms_widget && {
      id: 'call',
      icon: <Phone className="h-6 w-6 text-white" />,
      label: "Call",
      sublabel: profile.phone,
      url: `tel:${profile.phone}`,
      iconBgColor: "bg-blue-600",
      gradientColors: "from-blue-600/10 via-blue-600/5 to-transparent"
    },
    profile.phone && profile.call_sms_widget && {
      id: 'sms',
      icon: <MessageSquare className="h-6 w-6 text-white" />,
      label: "SMS",
      sublabel: profile.phone,
      url: `sms:${profile.phone}`,
      iconBgColor: "bg-blue-500",
      gradientColors: "from-blue-500/10 via-blue-500/5 to-transparent"
    },
    profile.facebook_url && {
      id: 'facebook',
      icon: <Facebook className="h-6 w-6 text-white" />,
      label: "Facebook",
      sublabel: "Connect with me",
      url: profile.facebook_url,
      iconBgColor: "bg-blue-600",
      gradientColors: "from-blue-600/10 via-blue-600/5 to-transparent"
    },
    profile.instagram_url && {
      id: 'instagram',
      icon: <Instagram className="h-6 w-6 text-white" />,
      label: "Instagram",
      sublabel: "Follow me",
      url: profile.instagram_url,
      iconBgColor: "bg-gradient-to-r from-purple-500 to-pink-500",
      gradientColors: "from-purple-500/10 via-pink-500/5 to-transparent"
    },
    profile.linkedin_url && {
      id: 'linkedin',
      icon: <Linkedin className="h-6 w-6 text-white" />,
      label: "LinkedIn",
      sublabel: "Connect with me",
      url: profile.linkedin_url,
      iconBgColor: "bg-blue-700",
      gradientColors: "from-blue-700/10 via-blue-700/5 to-transparent"
    },
    profile.youtube_url && {
      id: 'youtube',
      icon: <Youtube className="h-6 w-6 text-white" />,
      label: "YouTube",
      sublabel: "Watch my videos",
      url: profile.youtube_url,
      iconBgColor: "bg-red-600",
      gradientColors: "from-red-600/10 via-red-600/5 to-transparent"
    },
  ].filter(Boolean) as ContactOption[];

  const primaryOptions = contactOptions.filter(option => option.isPrimary);
  const secondaryOptions = contactOptions.filter(option => !option.isPrimary);

  const navigationButtons = [
    {
      id: 'contact',
      icon: <Info className="h-5 w-5" />,
      label: 'Contact Details',
      page: 'contact' as WidgetPage,
      show: true
    },
    {
      id: 'faqs',
      icon: <List className="h-5 w-5" />,
      label: 'FAQ',
      page: 'faqs' as WidgetPage,
      show: profile.show_faqs && profile.faqs ? Array.isArray(profile.faqs) && profile.faqs.length > 0 : false
    }
  ].filter(btn => btn.show);

  return (
    <>
      <div className="fixed bottom-6 right-6 flex items-center gap-3 z-50">
        <AnimatePresence>
          {showText && <WelcomeMessage primaryColor={primaryColor} secondaryColor={secondaryColor} />}
        </AnimatePresence>
        <FloatingButton 
          onClick={() => setOpen(true)} 
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
        />
      </div>

      <Dialog 
        open={open} 
        onOpenChange={(value) => {
          setOpen(value);
          if (!value) {
            setFormType(null);
            setFormData({
              name: "",
              email: "",
              phone: "",
              scheduledDate: "",
              message: "",
            });
            setCurrentPage('contact');
          }
        }}
      >
        <DialogContent 
          className="sm:max-w-md border-blue-500/20 animate-scale-in p-0 gap-0 overflow-hidden rounded-2xl"
          style={{
            '--theme-primary': primaryColor,
            '--theme-secondary': secondaryColor,
          } as React.CSSProperties}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/90 via-white to-purple-50/90 rounded-2xl -z-10" />
          
          {formType === 'ai' ? (
            <AIChatDialog profileId={profile.id} onBack={() => setFormType(null)} />
          ) : (
            <>
              <div className="p-5 sm:p-6 border-b border-blue-100/20 backdrop-blur-sm">
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle className="text-2xl font-semibold tracking-tight">
                      <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        {formType === null ? "Let's Connect" : 
                         formType === 'lead' ? "Leave a Message" : 
                         "Schedule Meeting"}
                      </span>
                    </DialogTitle>
                    {navigationButtons.length > 1 && (
                      <div className="flex gap-2 p-1 bg-gray-50 rounded-lg">
                        {navigationButtons.map((btn) => (
                          <button
                            key={btn.id}
                            onClick={() => setCurrentPage(btn.page)}
                            className={`flex items-center gap-2 p-2 rounded-lg transition-all duration-300 ${
                              currentPage === btn.page 
                                ? 'bg-white shadow text-blue-600 scale-105' 
                                : 'hover:bg-white/50 text-gray-600'
                            }`}
                          >
                            {btn.icon}
                            <span className="text-sm font-medium">{btn.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </DialogHeader>
              </div>

              {formType === null ? (
                <div className="max-h-[65vh] overflow-y-auto scrollbar-thin scrollbar-track-blue-100/20 scrollbar-thumb-blue-300/20 hover:scrollbar-thumb-blue-400/30 scroll-smooth">
                  {currentPage === 'contact' ? (
                    <div className="p-5 sm:p-6 space-y-6">
                      {primaryOptions.length > 0 && (
                        <div className="space-y-4">
                          <h3 className="text-sm font-medium text-gray-500">Connect Directly</h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {primaryOptions.map((option) => (
                              <motion.button
                                key={option.id}
                                onClick={() => {
                                  if (option.action) {
                                    option.action();
                                  } else if (option.url) {
                                    window.open(option.url, '_blank');
                                  }
                                  if (!option.action) setOpen(false);
                                }}
                                className="w-full flex items-center space-x-4 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 transition-all duration-300 group relative overflow-hidden"
                                whileHover={{ scale: 1.02, y: -2 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-200/30 via-purple-200/30 to-blue-200/30 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-300" />
                                <motion.div 
                                  className={`p-3 rounded-xl ${option.iconBgColor} group-hover:ring-2 ring-blue-200 transition-all duration-300 relative z-10`}
                                  whileHover={{ scale: 1.1, rotate: [0, -10, 10, 0] }}
                                >
                                  {option.icon}
                                </motion.div>
                                <div className="flex-1 text-left relative z-10">
                                  <div className="font-semibold text-gray-900 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                                    {option.label}
                                  </div>
                                  <div className="text-sm text-gray-500 font-medium">{option.sublabel}</div>
                                </div>
                                <motion.div
                                  className="text-blue-600"
                                  animate={{ x: [0, 5, 0] }}
                                  transition={{ repeat: Infinity, duration: 1.5 }}
                                >
                                  →
                                </motion.div>
                              </motion.button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {secondaryOptions.length > 0 && (
                        <div className="space-y-4 pt-4 border-t border-blue-100/20">
                          <h3 className="text-sm font-medium text-gray-500">Other Ways to Connect</h3>
                          <div className="grid grid-cols-1 gap-3">
                            {secondaryOptions.map((option) => (
                              <motion.button
                                key={option.id}
                                onClick={() => {
                                  if (option.action) {
                                    option.action();
                                  } else if (option.url) {
                                    window.open(option.url, '_blank');
                                  }
                                  if (!option.action) setOpen(false);
                                }}
                                className="w-full flex items-center space-x-4 p-3 rounded-xl hover:bg-white/70 transition-all duration-300 group relative overflow-hidden"
                                whileHover={{ scale: 1.01, y: -1 }}
                                whileTap={{ scale: 0.99 }}
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-200/20 via-purple-200/20 to-blue-200/20 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-300" />
                                <motion.div 
                                  className={`p-2.5 rounded-xl ${option.iconBgColor} group-hover:ring-2 ring-blue-200 transition-all duration-300 relative z-10`}
                                  whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
                                >
                                  {option.icon}
                                </motion.div>
                                <div className="flex-1 text-left relative z-10">
                                  <div className="font-medium text-gray-900">{option.label}</div>
                                  <div className="text-sm text-gray-500">{option.sublabel}</div>
                                </div>
                                <motion.div
                                  className="text-blue-500"
                                  animate={{ x: [0, 3, 0] }}
                                  transition={{ repeat: Infinity, duration: 2 }}
                                >
                                  →
                                </motion.div>
                              </motion.button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <FAQAccordion faqs={profile.faqs} />
                  )}
                </div>
              ) : (
                <ContactForm
                  formData={formData}
                  formType={formType}
                  submitting={submitting}
                  onChange={handleChange}
                  onSubmit={handleSubmit}
                />
              )}
            </>
          )}

          <div className="p-4 border-t border-blue-100/20 backdrop-blur-sm">
            <motion.div 
              className="text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <span className="text-xs font-medium tracking-wide text-gray-400">
                Powered by{' '}
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  GetListSpace
                </span>
              </span>
            </motion.div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
